// The sentence stem shares one contenteditable with the student's response, so
// nothing stops the caret from landing inside the stem -- and once it is there,
// a backspace shreds text the student does not own and the repair rewrites
// innerHTML, which drops the caret back at character zero.
//
// These helpers treat the stem as a read-only prefix: the caret is clamped to
// the first character the student actually owns, and deletions that would reach
// back past that boundary are dropped before the browser applies them.
import EditCaretPositioning from '../../Shared/libs/editCaretPositioning'

const BACKSPACE = 'Backspace'
const DELETE = 'Delete'

// Chrome fires `beforeinput` for soft-keyboard and IME deletions that never
// surface a usable `key` on keydown, so the same boundary is enforced there.
const BACKWARD_DELETE_INPUT_TYPES = [
  'deleteContentBackward',
  'deleteWordBackward',
  'deleteSoftLineBackward',
  'deleteHardLineBackward',
  'deleteEntireSoftLine'
]
const FORWARD_DELETE_INPUT_TYPES = [
  'deleteContent',
  'deleteContentForward',
  'deleteWordForward',
  'deleteSoftLineForward',
  'deleteHardLineForward',
  'deleteByCut',
  'deleteByDrag'
]

// These run to the top of the visual line or the paragraph rather than to the
// caret, and the stem shares its paragraph with the response, so how far they
// reach has nothing to do with where the caret sits.
const LINE_DELETE_INPUT_TYPES = [
  'deleteSoftLineBackward',
  'deleteHardLineBackward',
  'deleteEntireSoftLine'
]

// Measures the stem the way the browser will render it, so entities and any
// rich text tags count for exactly as many characters as the caret will see.
export const renderedTextLength = (html: string) => {
  const container = document.createElement('div')
  container.innerHTML = html || ''
  return container.textContent.length
}

export const editorSelectionOffsets = (editor) => {
  if (!editor) { return null }

  const selection = window.getSelection && window.getSelection()

  if (!selection || !selection.rangeCount) { return null }
  if (!editor.contains(selection.anchorNode)) { return null }

  return EditCaretPositioning.saveSelection(editor)
}

// The character offset of a DOM position within the editor, measured the same
// way the caret offsets are, so the two can be compared.
export const offsetWithinEditor = (editor, node, nodeOffset: number) => {
  if (!editor || !node || !editor.contains(node)) { return null }

  const range = document.createRange()
  range.selectNodeContents(editor)
  range.setEnd(node, nodeOffset)

  return range.toString().length
}

export const placeCaretAtOffset = (editor, offset: number) => {
  if (!editor) { return }

  EditCaretPositioning.restoreSelection(editor, { start: offset, end: offset })
}

// Pulls a caret (or the leading edge of a selection) out of the stem and onto
// the boundary, which is what keeps clicks, arrow keys, Home and select-all
// from ever putting the student somewhere they cannot type.
export const clampSelectionToStem = (editor, stemLength: number) => {
  const offsets = editorSelectionOffsets(editor)

  if (!offsets || offsets.start >= stemLength) { return false }

  EditCaretPositioning.restoreSelection(editor, {
    start: stemLength,
    end: Math.max(offsets.end, stemLength)
  })

  return true
}

const deletionWouldCrossBoundary = (offsets, stemLength: number, deletesBackward: boolean) => {
  if (offsets.start !== offsets.end) { return offsets.start < stemLength }

  return deletesBackward ? offsets.start <= stemLength : offsets.start < stemLength
}

export const keystrokeWouldEditStem = (event, editor, stemLength: number) => {
  const { key, } = event

  if (key !== BACKSPACE && key !== DELETE) { return false }

  const offsets = editorSelectionOffsets(editor)

  if (!offsets) { return false }

  return deletionWouldCrossBoundary(offsets, stemLength, key === BACKSPACE)
}

// beforeinput hands over the exact ranges the browser is about to remove, which
// is the only dependable way to judge a word- or line-wise deletion.
const earliestTargetOffset = (event, editor) => {
  if (typeof event.getTargetRanges !== 'function') { return null }

  const ranges = event.getTargetRanges()

  if (!ranges || !ranges.length) { return null }

  const offsets = ranges
    .map((range) => offsetWithinEditor(editor, range.startContainer, range.startOffset))
    .filter((offset) => offset !== null)

  return offsets.length ? Math.min(...offsets) : null
}

export const inputWouldEditStem = (event, editor, stemLength: number) => {
  const { inputType, } = event
  const deletesBackward = BACKWARD_DELETE_INPUT_TYPES.includes(inputType)

  if (!deletesBackward && !FORWARD_DELETE_INPUT_TYPES.includes(inputType)) { return false }

  const targetOffset = earliestTargetOffset(event, editor)

  if (targetOffset !== null) { return targetOffset < stemLength }

  // Without target ranges (older Safari) a backward line-wise delete has to be
  // assumed to reach the stem, since it runs to the top of the shared paragraph.
  if (LINE_DELETE_INPUT_TYPES.includes(inputType)) { return true }

  const offsets = editorSelectionOffsets(editor)

  if (!offsets) { return false }

  return deletionWouldCrossBoundary(offsets, stemLength, deletesBackward)
}
